import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { getFreshAccessToken } from '../../../utils/authClient';

// MFA self-service: status, enrol (QR + verify), disable.
// These endpoints authenticate with the Cognito ACCESS token, not the ID token,
// so they cannot go through apiFetch - getFreshAccessToken gives them the same
// refresh-before-use behaviour.
//
// Enrolment and disable both step up: the backend demands the password again
// (plus a current authenticator code for disable), so a bearer token alone -
// stolen from localStorage or an unattended browser - cannot change MFA.
const SecurityModal = ({ isOpen, onClose, apiUrl, onStatusChange }) => {
    const [loading, setLoading] = useState(true);
    const [mfaEnabled, setMfaEnabled] = useState(false);
    const [enrolling, setEnrolling] = useState(false);
    const [enrolSuccess, setEnrolSuccess] = useState(false);
    const [secretCode, setSecretCode] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    // Step-up prompt: null | 'enrol' (password) | 'disable' (password + code)
    const [promptMode, setPromptMode] = useState(null);
    const [password, setPassword] = useState('');
    const [disableCode, setDisableCode] = useState('');

    const email = localStorage.getItem('clientEmail');

    // Throws SESSION_EXPIRED when there is no refreshable session - callers
    // surface a clear re-login message instead of sending "Bearer null" and
    // failing with a generic error.
    const authHeaders = async () => {
        const accessToken = await getFreshAccessToken();
        if (!accessToken) {
            throw new Error('SESSION_EXPIRED');
        }
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
        };
    };

    const SESSION_EXPIRED_MSG = 'Your session has expired. Please log out and back in, then retry.';

    useEffect(() => {
        if (!isOpen) return;
        setError('');
        setEnrolling(false);
        setEnrolSuccess(false);
        setCode('');
        setPromptMode(null);
        setPassword('');
        setDisableCode('');
        setLoading(true);
        (async () => {
            try {
                const res = await fetch(`${apiUrl}/client/mfa/status`, { headers: await authHeaders() });
                if (!res.ok) throw new Error(`status ${res.status}`);
                const data = await res.json();
                setMfaEnabled(data.mfaEnabled === true);
                if (onStatusChange) onStatusChange(data.mfaEnabled === true);
            } catch (err) {
                console.error('MFA status load failed:', err);
                setError(err.message === 'SESSION_EXPIRED'
                    ? SESSION_EXPIRED_MSG
                    : 'Could not load MFA status. Please log out and back in, then retry.');
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, apiUrl]);

    const startEnrol = async () => {
        setError('');
        setBusy(true);
        try {
            const res = await fetch(`${apiUrl}/client/mfa/enable`, {
                method: 'POST',
                headers: await authHeaders(),
                body: JSON.stringify({ password })
            });
            const data = await res.json();
            if (res.ok) {
                setSecretCode(data.secretCode);
                setEnrolling(true);
                setPromptMode(null);
                setPassword('');
            } else {
                setError(data.error || 'Failed to start MFA setup');
            }
        } catch (err) {
            setError(err.message === 'SESSION_EXPIRED' ? SESSION_EXPIRED_MSG : 'Connection error. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    const verifyEnrol = async () => {
        setError('');
        setBusy(true);
        try {
            const res = await fetch(`${apiUrl}/client/mfa/verify`, {
                method: 'POST',
                headers: await authHeaders(),
                body: JSON.stringify({ code })
            });
            const data = await res.json();
            if (res.ok) {
                setMfaEnabled(true);
                setEnrolling(false);
                setEnrolSuccess(true);
                setCode('');
                if (onStatusChange) onStatusChange(true);
            } else {
                setError(data.error || 'Verification failed');
            }
        } catch (err) {
            setError(err.message === 'SESSION_EXPIRED' ? SESSION_EXPIRED_MSG : 'Connection error. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    // Requires the step-up credentials: password + a current authenticator code
    const disableMfa = async () => {
        setError('');
        setBusy(true);
        try {
            const res = await fetch(`${apiUrl}/client/mfa/disable`, {
                method: 'POST',
                headers: await authHeaders(),
                body: JSON.stringify({ password, code: disableCode })
            });
            const data = await res.json();
            if (res.ok) {
                setMfaEnabled(false);
                setEnrolSuccess(false);
                setPromptMode(null);
                setPassword('');
                setDisableCode('');
                if (onStatusChange) onStatusChange(false);
            } else {
                setError(data.error || 'Failed to disable MFA');
            }
        } catch (err) {
            setError(err.message === 'SESSION_EXPIRED' ? SESSION_EXPIRED_MSG : 'Connection error. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    if (!isOpen) return null;

    const otpauthUri = `otpauth://totp/${encodeURIComponent('Migration Experts Portal')}:${encodeURIComponent(email || '')}?secret=${secretCode}&issuer=${encodeURIComponent('The Migration Experts')}`;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-8 max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Multi-Factor Authentication</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
            {error}
            </div>
        )}

        {loading ? (
            <p className="text-gray-600">Loading…</p>
        ) : enrolSuccess ? (
            <>
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-800 rounded text-center">
            <div className="text-3xl mb-2">✓</div>
            <p className="font-medium">MFA enabled</p>
            <p className="text-sm mt-1">You&apos;ll be asked for an authenticator code at each login.</p>
            </div>
            <button
            onClick={onClose}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700"
            >
            Done
            </button>
            <button
            onClick={() => { setEnrolSuccess(false); setError(''); setPromptMode('disable'); }}
            disabled={busy}
            className="w-full mt-3 text-sm text-red-700 hover:underline disabled:opacity-50"
            >
            Disable MFA
            </button>
            </>
        ) : enrolling ? (
            <>
            <p className="text-gray-600 mb-4">
            Scan this QR code with your authenticator app, then enter the 6-digit code to finish setup.
            </p>
            <div className="flex justify-center mb-4">
            <QRCodeSVG value={otpauthUri} size={180} />
            </div>
            <p className="text-xs text-gray-500 mb-4 break-all">
            Manual entry key: {secretCode}
            </p>
            <input
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Authenticator code"
            className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoComplete="one-time-code"
            />
            <div className="flex gap-3">
            <button
            onClick={verifyEnrol}
            disabled={busy || !code}
            className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
            {busy ? 'Verifying…' : 'Verify & Enable'}
            </button>
            <button
            onClick={() => { setEnrolling(false); setCode(''); setError(''); }}
            className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
            Cancel
            </button>
            </div>
            </>
        ) : promptMode === 'enrol' ? (
            <>
            <p className="text-gray-600 mb-4">
            Confirm your password to start setting up multi-factor authentication.
            </p>
            <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
            className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoComplete="current-password"
            />
            <div className="flex gap-3">
            <button
            onClick={startEnrol}
            disabled={busy || !password}
            className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
            {busy ? 'Checking…' : 'Continue'}
            </button>
            <button
            onClick={() => { setPromptMode(null); setPassword(''); setError(''); }}
            className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
            Cancel
            </button>
            </div>
            </>
        ) : promptMode === 'disable' ? (
            <>
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm">
            Disabling MFA leaves this account protected by password only.
            </div>
            <p className="text-gray-600 mb-4">
            To confirm, enter your password and a current authenticator code.
            </p>
            <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
            className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoComplete="current-password"
            />
            <input
            type="text"
            inputMode="numeric"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            placeholder="Authenticator code"
            className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoComplete="one-time-code"
            />
            <div className="flex gap-3">
            <button
            onClick={disableMfa}
            disabled={busy || !password || !disableCode}
            className="flex-1 py-2 px-4 border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50"
            >
            {busy ? 'Disabling…' : 'Disable MFA'}
            </button>
            <button
            onClick={() => { setPromptMode(null); setPassword(''); setDisableCode(''); setError(''); }}
            className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
            Cancel
            </button>
            </div>
            </>
        ) : (
            <>
            <div className="mb-6">
            <p className="text-gray-800 font-medium mb-1">Multi-factor authentication (MFA)</p>
            <p className="text-sm text-gray-600 mb-3">
            Adds a second login step using an authenticator app. Recommended for all accounts.
            </p>
            <p className="text-sm">
            Status:{' '}
            <span className={mfaEnabled ? 'text-green-700 font-medium' : 'text-gray-700 font-medium'}>
            {mfaEnabled ? 'Enabled' : 'Not enabled'}
            </span>
            </p>
            </div>
            {mfaEnabled ? (
                <button
                onClick={() => { setError(''); setPromptMode('disable'); }}
                disabled={busy}
                className="w-full py-2 px-4 border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50"
                >
                Disable MFA
                </button>
            ) : (
                <button
                onClick={() => { setError(''); setPromptMode('enrol'); }}
                disabled={busy}
                className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                Enable MFA
                </button>
            )}
            </>
        )}
        </div>
        </div>
    );
};

export default SecurityModal;
